import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ChevronDown,
  ChevronUp,
  Pause,
  Play,
  RefreshCw,
  TrendingDown,
  TrendingUp,
} from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { buildMonthsList, clearPnLCache, fetchPnL } from '../api';
import { pnlKeys } from '../hooks';
import type { MonthEntry, PnLReport, PnLRow } from '../types';
import { cn, formatCurrency } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useUserUiPref } from '@/hooks/useUserUiPref';

const PNL_TABS = [
  { value: 'monthly', label: 'Monthly' },
  { value: 'comparative', label: 'Comparative' },
] as const;
type PnLTab = (typeof PNL_TABS)[number]['value'];
const VALID_PNL_TABS: readonly PnLTab[] = PNL_TABS.map((t) => t.value);

function isValidPnLTab(v: unknown): v is PnLTab {
  return typeof v === 'string' && (VALID_PNL_TABS as readonly string[]).includes(v);
}

interface MonthData {
  revenue: number;
  cogs: number;
  grossProfit: number;
  expenses: number;
  otherIncome: number;
  otherExpenses: number;
  netIncome: number;
  detailed: PnLReport;
}

export default function PnLDashboard() {
  const queryClient = useQueryClient();

  const [tabRaw, setTabRaw] = useUserUiPref<PnLTab>('pnl.tab', 'monthly');
  const tab: PnLTab = isValidPnLTab(tabRaw) ? tabRaw : 'monthly';
  const setTab = setTabRaw as (next: PnLTab) => void;
  const [comparativeExpanded, setComparativeExpanded] = useUserUiPref<string[]>('pnl.comparativeExpanded', []);
  const [months, setMonths] = useState(12);
  const [expandedMonth, setExpandedMonth] = useState<string | null>(null);
  const [loaded, setLoaded] = useState<Record<string, MonthData>>({});
  const [currentLoading, setCurrentLoading] = useState<string | null>(null);
  const [isPaused, setIsPaused] = useState(false);

  const monthsList: MonthEntry[] = useMemo(() => buildMonthsList(months), [months]);
  const monthsNewestFirst = useMemo(() => [...monthsList].reverse(), [monthsList]);

  const getNextMonthToLoad = useCallback((): MonthEntry | null => {
    for (const m of monthsNewestFirst) {
      if (!loaded[m.month]) return m;
    }
    return null;
  }, [monthsNewestFirst, loaded]);

  // Progressive loader
  useEffect(() => {
    if (isPaused || currentLoading) return;
    const next = getNextMonthToLoad();
    if (!next) return;

    const timer = setTimeout(async () => {
      setCurrentLoading(next.month);
      try {
        const data = await queryClient.fetchQuery({
          queryKey: pnlKeys.month(next.startDate, next.endDate),
          queryFn: () => fetchPnL(next.startDate, next.endDate),
          staleTime: 1000 * 60 * 5,
        });
        setLoaded((prev) => ({
          ...prev,
          [next.month]: {
            revenue: data.totalIncome,
            cogs: data.totalCogs,
            grossProfit: data.grossProfit,
            expenses: data.totalExpenses,
            otherIncome: data.totalOtherIncome,
            otherExpenses: data.totalOtherExpenses,
            netIncome: data.netIncome,
            detailed: data,
          },
        }));
      } catch {
        // mark as attempted with empty data so we don't spin forever
        setLoaded((prev) => ({
          ...prev,
          [next.month]: {
            revenue: 0,
            cogs: 0,
            grossProfit: 0,
            expenses: 0,
            otherIncome: 0,
            otherExpenses: 0,
            netIncome: 0,
            detailed: {
              companyName: '',
              reportName: '',
              startDate: next.startDate,
              endDate: next.endDate,
              currency: 'CAD',
              income: [],
              cogs: [],
              expenses: [],
              otherIncome: [],
              otherExpenses: [],
              totalIncome: 0,
              totalCogs: 0,
              grossProfit: 0,
              totalExpenses: 0,
              totalOtherIncome: 0,
              totalOtherExpenses: 0,
              netIncome: 0,
            },
          },
        }));
      } finally {
        setCurrentLoading(null);
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [isPaused, currentLoading, getNextMonthToLoad, queryClient]);

  const handleRefresh = useCallback(async () => {
    // Clear both the server-side in-memory cache and the TanStack Query cache,
    // then restart the progressive loader.
    try {
      await clearPnLCache();
    } catch {
      // best-effort — still refresh the client cache
    }
    queryClient.invalidateQueries({ queryKey: pnlKeys.all });
    setLoaded({});
    setCurrentLoading(null);
    setIsPaused(false);
  }, [queryClient]);

  const totals = Object.values(loaded).reduce(
    (acc, m) => ({
      revenue: acc.revenue + m.revenue,
      cogs: acc.cogs + m.cogs,
      grossProfit: acc.grossProfit + m.grossProfit,
      expenses: acc.expenses + m.expenses,
      otherIncome: acc.otherIncome + m.otherIncome,
      otherExpenses: acc.otherExpenses + m.otherExpenses,
      netIncome: acc.netIncome + m.netIncome,
    }),
    { revenue: 0, cogs: 0, grossProfit: 0, expenses: 0, otherIncome: 0, otherExpenses: 0, netIncome: 0 },
  );

  const loadedCount = Object.keys(loaded).length;
  const totalCount = monthsList.length;
  const isAllLoaded = loadedCount === totalCount;

  return (
    <div className="space-y-4">
      {/* Tab bar + toolbar (Rocks-style single row) */}
      <div className="flex items-center justify-between gap-2 border-b border-border flex-wrap">
        <div className="flex items-center gap-0">
          {PNL_TABS.map((t) => (
            <button
              key={t.value}
              onClick={() => setTab(t.value)}
              className={cn(
                'px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors',
                tab === t.value
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border',
              )}
            >
              {t.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 flex-wrap pb-1.5">
          <span className="text-xs text-muted-foreground">
            {isAllLoaded ? `${totalCount} months` : `Loading ${loadedCount}/${totalCount}…`}
          </span>
          {!isAllLoaded && (
            <Button variant="outline" size="sm" onClick={() => setIsPaused((p) => !p)}>
              {isPaused ? <Play className="w-4 h-4 mr-1" /> : <Pause className="w-4 h-4 mr-1" />}
              {isPaused ? 'Resume' : 'Pause'}
            </Button>
          )}
          <Select
            value={String(months)}
            onValueChange={(v) => {
              setMonths(parseInt(v));
              setLoaded({});
              setCurrentLoading(null);
              setIsPaused(false);
            }}
          >
            <SelectTrigger className="h-8 w-[160px] text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="6">Last 6 months</SelectItem>
              <SelectItem value="12">Last 12 months</SelectItem>
              <SelectItem value="24">Last 24 months</SelectItem>
            </SelectContent>
          </Select>
          <Button size="sm" onClick={handleRefresh}>
            <RefreshCw className="w-4 h-4 mr-1" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Progress */}
      {!isAllLoaded && (
        <div className="w-full bg-muted rounded-full h-1.5">
          <div
            className="bg-blue-600 h-1.5 rounded-full transition-all"
            style={{ width: `${(loadedCount / totalCount) * 100}%` }}
          />
        </div>
      )}

      {/* Summary cards */}
      <div className="grid grid-cols-5 gap-4">
        <SummaryCard title="Revenue" value={totals.revenue} />
        <SummaryCard title="COGS" value={totals.cogs} />
        <SummaryCard title="Gross Profit" value={totals.grossProfit} trend={totals.grossProfit > 0 ? 'up' : 'down'} />
        <SummaryCard title="Expenses" value={totals.expenses} />
        <SummaryCard title="Net Income" value={totals.netIncome} trend={totals.netIncome > 0 ? 'up' : 'down'} />
      </div>

      {tab === 'comparative' ? (
        <ComparativeTable
          monthsList={monthsList}
          loaded={loaded}
          totals={totals}
          loadedCount={loadedCount}
          totalCount={totalCount}
          isAllLoaded={isAllLoaded}
          currentLoading={currentLoading}
          expanded={comparativeExpanded}
          onExpandedChange={setComparativeExpanded}
        />
      ) : (
      /* Monthly table */
      <div className="bg-background rounded-xl shadow-sm border overflow-hidden">
        <table className="w-full">
          <thead className="bg-muted/50 border-b">
            <tr className="text-left text-xs font-semibold text-muted-foreground">
              <th className="px-3 py-3">Month</th>
              <th className="px-3 py-3 text-right">Revenue</th>
              <th className="px-3 py-3 text-right">COGS</th>
              <th className="px-3 py-3 text-right">Gross Profit</th>
              <th className="px-3 py-3 text-right">Expenses</th>
              <th className="px-3 py-3 text-right">Other</th>
              <th className="px-3 py-3 text-right">Net Income</th>
              <th className="px-3 py-3 text-right">Margin</th>
              <th className="w-8" />
            </tr>
          </thead>
          <tbody>
            {monthsList.map((month, idx) => {
              const data = loaded[month.month];
              const isExpanded = expandedMonth === month.month;
              const isCurrentlyLoading = currentLoading === month.month;
              const margin = data && data.revenue > 0 ? (data.netIncome / data.revenue) * 100 : 0;

              return (
                <>
                  <tr
                    key={month.month}
                    onClick={() => setExpandedMonth(isExpanded ? null : month.month)}
                    className={cn(
                      'border-b hover:bg-muted/40 cursor-pointer transition-colors text-sm',
                      idx % 2 === 0 ? 'bg-background' : 'bg-muted/10',
                      isExpanded && 'bg-blue-50 dark:bg-blue-950/20',
                      isCurrentlyLoading && 'bg-yellow-50 dark:bg-yellow-950/20',
                    )}
                  >
                    <td className="px-3 py-3 font-medium">
                      <div className="flex items-center gap-2">
                        {month.label}
                        {isCurrentlyLoading && <RefreshCw className="w-3 h-3 animate-spin text-blue-500" />}
                      </div>
                    </td>
                    {data ? (
                      <>
                        <td className="px-3 py-3 text-right tabular-nums">{formatCurrency(data.revenue)}</td>
                        <td className="px-3 py-3 text-right tabular-nums text-orange-600">{formatCurrency(data.cogs)}</td>
                        <td className="px-3 py-3 text-right tabular-nums font-medium text-blue-600">{formatCurrency(data.grossProfit)}</td>
                        <td className="px-3 py-3 text-right tabular-nums">{formatCurrency(data.expenses)}</td>
                        <td className="px-3 py-3 text-right tabular-nums text-purple-600">{formatCurrency(data.otherIncome - data.otherExpenses)}</td>
                        <td className={cn('px-3 py-3 text-right tabular-nums font-medium', data.netIncome >= 0 ? 'text-green-600' : 'text-red-600')}>
                          {formatCurrency(data.netIncome)}
                        </td>
                        <td className="px-3 py-3 text-right tabular-nums text-muted-foreground">{margin.toFixed(1)}%</td>
                      </>
                    ) : (
                      <>
                        {Array.from({ length: 7 }).map((_, i) => (
                          <td key={i} className="px-3 py-3 text-right text-muted-foreground/40">—</td>
                        ))}
                      </>
                    )}
                    <td className="px-2 py-3 text-muted-foreground">
                      {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </td>
                  </tr>
                  {isExpanded && data?.detailed && (
                    <tr key={`${month.month}-detail`}>
                      <td colSpan={9} className="bg-muted/30 p-6 border-b">
                        <DetailedPnL data={data.detailed} />
                      </td>
                    </tr>
                  )}
                </>
              );
            })}
          </tbody>
          {loadedCount > 0 && (
            <tfoot className="bg-muted font-semibold text-sm">
              <tr>
                <td className="px-3 py-3">Total{!isAllLoaded && ` (${loadedCount}/${totalCount})`}</td>
                <td className="px-3 py-3 text-right tabular-nums">{formatCurrency(totals.revenue)}</td>
                <td className="px-3 py-3 text-right tabular-nums text-orange-600">{formatCurrency(totals.cogs)}</td>
                <td className="px-3 py-3 text-right tabular-nums text-blue-600">{formatCurrency(totals.grossProfit)}</td>
                <td className="px-3 py-3 text-right tabular-nums">{formatCurrency(totals.expenses)}</td>
                <td className="px-3 py-3 text-right tabular-nums text-purple-600">{formatCurrency(totals.otherIncome - totals.otherExpenses)}</td>
                <td className={cn('px-3 py-3 text-right tabular-nums', totals.netIncome >= 0 ? 'text-green-600' : 'text-red-600')}>
                  {formatCurrency(totals.netIncome)}
                </td>
                <td className="px-3 py-3 text-right tabular-nums text-muted-foreground">
                  {totals.revenue ? ((totals.netIncome / totals.revenue) * 100).toFixed(1) : 0}%
                </td>
                <td />
              </tr>
            </tfoot>
          )}
        </table>
      </div>
      )}
    </div>
  );
}

interface Totals {
  revenue: number;
  cogs: number;
  grossProfit: number;
  expenses: number;
  otherIncome: number;
  otherExpenses: number;
  netIncome: number;
}

function ComparativeTable({
  monthsList,
  loaded,
  totals,
  loadedCount,
  totalCount,
  isAllLoaded,
  currentLoading,
  expanded,
  onExpandedChange,
}: {
  monthsList: MonthEntry[];
  loaded: Record<string, MonthData>;
  totals: Totals;
  loadedCount: number;
  totalCount: number;
  isAllLoaded: boolean;
  currentLoading: string | null;
  expanded: string[];
  onExpandedChange: (next: string[] | ((prev: string[]) => string[])) => void;
}) {
  // Months newest → oldest left to right
  const months = useMemo(() => [...monthsList].reverse(), [monthsList]);
  const expandedSet = useMemo(() => new Set(expanded), [expanded]);

  function toggle(key: string) {
    onExpandedChange((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
  }

  type DetailKey = 'income' | 'cogs' | 'expenses' | 'otherIncome' | 'otherExpenses';
  type RowSpec = {
    key: keyof MonthData | 'margin';
    label: string;
    kind: 'currency' | 'percent';
    valueClass?: string;
    rowClass?: string;
    signed?: boolean;
    detailFrom?: DetailKey;
  };

  const rows: RowSpec[] = [
    { key: 'revenue', label: 'Revenue', kind: 'currency', detailFrom: 'income' },
    { key: 'cogs', label: 'COGS', kind: 'currency', valueClass: 'text-orange-600', detailFrom: 'cogs' },
    { key: 'grossProfit', label: 'Gross Profit', kind: 'currency', valueClass: 'text-blue-600', rowClass: 'font-medium' },
    { key: 'expenses', label: 'Expenses', kind: 'currency', detailFrom: 'expenses' },
    { key: 'otherIncome', label: 'Other Income', kind: 'currency', valueClass: 'text-purple-600', detailFrom: 'otherIncome' },
    { key: 'otherExpenses', label: 'Other Expenses', kind: 'currency', valueClass: 'text-purple-600', detailFrom: 'otherExpenses' },
    { key: 'netIncome', label: 'Net Income', kind: 'currency', signed: true, rowClass: 'font-medium' },
    { key: 'margin', label: 'Margin %', kind: 'percent' },
  ];

  // months is newest-first; GL link still wants chronological start..end
  const overallStart = months[months.length - 1]?.startDate ?? '';
  const overallEnd = months[0]?.endDate ?? '';

  // Build per-account breakdown across all loaded months for a given category.
  // Key = accountId when present, otherwise normalized name.
  type AccountAgg = {
    key: string;
    name: string;
    accountId?: string;
    perMonth: Record<string, number>;
    total: number;
    firstSeenIdx: number;
  };

  function aggregate(detailKey: DetailKey): AccountAgg[] {
    const map = new Map<string, AccountAgg>();
    months.forEach((m, idx) => {
      const data = loaded[m.month];
      if (!data) return;
      const list = data.detailed[detailKey];
      for (const r of list) {
        if (r.type !== 'item') continue;
        const key = r.accountId || `name:${r.name}`;
        let agg = map.get(key);
        if (!agg) {
          agg = { key, name: r.name, accountId: r.accountId, perMonth: {}, total: 0, firstSeenIdx: idx };
          map.set(key, agg);
        }
        agg.perMonth[m.month] = (agg.perMonth[m.month] ?? 0) + r.value;
        agg.total += r.value;
      }
    });
    return [...map.values()].sort((a, b) => b.total - a.total || a.firstSeenIdx - b.firstSeenIdx);
  }

  function cellValue(spec: RowSpec, m?: MonthData): number | null {
    if (!m) return null;
    if (spec.key === 'margin') {
      return m.revenue > 0 ? (m.netIncome / m.revenue) * 100 : 0;
    }
    return m[spec.key] as number;
  }

  function totalValue(spec: RowSpec): number {
    if (spec.key === 'margin') {
      return totals.revenue > 0 ? (totals.netIncome / totals.revenue) * 100 : 0;
    }
    return totals[spec.key as keyof Totals];
  }

  function fmt(spec: RowSpec, value: number | null): string {
    if (value === null) return '—';
    return spec.kind === 'percent' ? `${value.toFixed(1)}%` : formatCurrency(value);
  }

  function colorFor(spec: RowSpec, value: number | null): string {
    if (value === null) return 'text-muted-foreground/40';
    if (spec.signed) return value >= 0 ? 'text-green-600' : 'text-red-600';
    return spec.valueClass ?? '';
  }

  return (
    <div className="bg-background rounded-xl shadow-sm border overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm border-separate border-spacing-0">
          <thead className="bg-muted/50">
            <tr className="text-xs font-semibold text-muted-foreground">
              <th className="sticky left-0 z-10 bg-muted/80 backdrop-blur px-3 py-3 text-left border-b min-w-[160px]">
                Line item
              </th>
              {months.map((m) => {
                const isCurrentlyLoading = currentLoading === m.month;
                return (
                  <th key={m.month} className="px-3 py-3 text-right border-b min-w-[110px]">
                    <div className="inline-flex items-center gap-1 justify-end">
                      {m.label}
                      {isCurrentlyLoading && <RefreshCw className="w-3 h-3 animate-spin text-blue-500" />}
                    </div>
                  </th>
                );
              })}
              <th className="sticky right-0 z-10 bg-muted/80 backdrop-blur px-3 py-3 text-right border-b border-l min-w-[120px]">
                Total{!isAllLoaded && ` (${loadedCount}/${totalCount})`}
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((spec, idx) => {
              const isExpandable = !!spec.detailFrom;
              const isOpen = isExpandable && expandedSet.has(String(spec.key));
              const stripe = idx % 2 === 0 ? 'bg-background' : 'bg-muted/10';
              const detailRows = isOpen && spec.detailFrom ? aggregate(spec.detailFrom) : [];
              return (
                <Fragment key={spec.key}>
                  <tr
                    className={cn(stripe, spec.rowClass, isExpandable && 'cursor-pointer hover:bg-muted/40', isOpen && 'bg-blue-50 dark:bg-blue-950/20')}
                    onClick={isExpandable ? () => toggle(String(spec.key)) : undefined}
                  >
                    <td className="sticky left-0 z-10 bg-inherit px-3 py-2 font-medium border-b">
                      <div className="flex items-center gap-1">
                        {isExpandable ? (
                          isOpen ? <ChevronUp className="w-3.5 h-3.5 text-muted-foreground" /> : <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" />
                        ) : (
                          <span className="w-3.5" />
                        )}
                        {spec.label}
                      </div>
                    </td>
                    {months.map((m) => {
                      const data = loaded[m.month];
                      const v = cellValue(spec, data);
                      return (
                        <td
                          key={m.month}
                          className={cn('px-3 py-2 text-right tabular-nums border-b', colorFor(spec, v))}
                        >
                          {fmt(spec, v)}
                        </td>
                      );
                    })}
                    <td
                      className={cn(
                        'sticky right-0 z-10 bg-inherit px-3 py-2 text-right tabular-nums font-semibold border-b border-l',
                        colorFor(spec, totalValue(spec)),
                      )}
                    >
                      {fmt(spec, totalValue(spec))}
                    </td>
                  </tr>
                  {isOpen && detailRows.map((agg) => (
                    <tr key={`${spec.key}-${agg.key}`} className="bg-muted/5 text-xs text-muted-foreground">
                      <td className="sticky left-0 z-10 bg-background px-3 py-1.5 border-b pl-8">
                        {agg.accountId ? (
                          <Link
                            to={`/acc/pnl/gl/${agg.accountId}?start=${overallStart}&end=${overallEnd}&name=${encodeURIComponent(agg.name)}`}
                            className="hover:text-blue-700 hover:underline"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {agg.name}
                          </Link>
                        ) : (
                          agg.name
                        )}
                      </td>
                      {months.map((m) => {
                        const data = loaded[m.month];
                        const v = data ? agg.perMonth[m.month] ?? 0 : null;
                        return (
                          <td key={m.month} className="px-3 py-1.5 text-right tabular-nums border-b">
                            {v === null ? '—' : v === 0 ? '' : formatCurrency(v)}
                          </td>
                        );
                      })}
                      <td className="sticky right-0 z-10 bg-background px-3 py-1.5 text-right tabular-nums border-b border-l">
                        {agg.total === 0 ? '' : formatCurrency(agg.total)}
                      </td>
                    </tr>
                  ))}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SummaryCard({ title, value, trend }: { title: string; value: number; trend?: 'up' | 'down' }) {
  return (
    <div className="bg-background rounded-xl shadow-sm border p-5">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{title}</span>
        {trend === 'up' && <TrendingUp className="w-4 h-4 text-green-500" />}
        {trend === 'down' && <TrendingDown className="w-4 h-4 text-red-500" />}
      </div>
      <div
        className={cn(
          'text-2xl font-bold mt-2 tabular-nums',
          trend === 'up' && value > 0 && 'text-green-600',
          trend === 'down' && value < 0 && 'text-red-600',
        )}
      >
        {formatCurrency(value)}
      </div>
    </div>
  );
}

function AccountRow({ row, startDate, endDate }: { row: PnLRow; startDate: string; endDate: string }) {
  const base = cn(
    'flex justify-between py-1',
    row.type === 'header' && 'font-medium text-foreground mt-2',
    row.type === 'subtotal' && 'font-medium text-muted-foreground border-t pt-2 mt-2',
    row.type === 'item' && 'text-muted-foreground',
  );
  const content = (
    <>
      <span className="truncate mr-2">{row.name}</span>
      {row.value !== 0 && <span className="tabular-nums shrink-0">{formatCurrency(row.value)}</span>}
    </>
  );
  if (row.accountId && row.type === 'item') {
    return (
      <Link
        to={`/acc/pnl/gl/${row.accountId}?start=${startDate}&end=${endDate}&name=${encodeURIComponent(row.name)}`}
        className={cn(base, 'hover:bg-blue-100 dark:hover:bg-blue-950/40 hover:text-blue-700 rounded px-1 -mx-1')}
        style={{ paddingLeft: `${row.depth * 12}px` }}
        onClick={(e) => e.stopPropagation()}
      >
        {content}
      </Link>
    );
  }
  return (
    <div className={base} style={{ paddingLeft: `${row.depth * 12}px` }}>
      {content}
    </div>
  );
}

function DetailedPnL({ data }: { data: PnLReport }) {
  const netOther = data.totalOtherIncome - data.totalOtherExpenses;
  return (
    <div className="grid grid-cols-4 gap-4 text-sm">
      <div>
        <h4 className="font-semibold mb-3">Income</h4>
        <div className="space-y-1">
          {data.income.map((row, idx) => (
            <AccountRow key={idx} row={row} startDate={data.startDate} endDate={data.endDate} />
          ))}
          <div className="flex justify-between py-2 font-bold text-green-600 border-t-2 mt-2">
            <span>Total Income</span>
            <span className="tabular-nums">{formatCurrency(data.totalIncome)}</span>
          </div>
        </div>
      </div>
      <div>
        <h4 className="font-semibold mb-3">Cost of Goods Sold</h4>
        <div className="space-y-1">
          {data.cogs.map((row, idx) => (
            <AccountRow key={idx} row={row} startDate={data.startDate} endDate={data.endDate} />
          ))}
          <div className="flex justify-between py-2 font-bold text-orange-600 border-t-2 mt-2">
            <span>Total COGS</span>
            <span className="tabular-nums">{formatCurrency(data.totalCogs)}</span>
          </div>
          <div className="flex justify-between py-2 font-bold text-blue-600 border-t mt-2">
            <span>Gross Profit</span>
            <span className="tabular-nums">{formatCurrency(data.grossProfit)}</span>
          </div>
        </div>
      </div>
      <div>
        <h4 className="font-semibold mb-3">Expenses</h4>
        <div className="space-y-1">
          {data.expenses.map((row, idx) => (
            <AccountRow key={idx} row={row} startDate={data.startDate} endDate={data.endDate} />
          ))}
          <div className="flex justify-between py-2 font-bold text-red-600 border-t-2 mt-2">
            <span>Total Expenses</span>
            <span className="tabular-nums">{formatCurrency(data.totalExpenses)}</span>
          </div>
        </div>
      </div>
      <div>
        <h4 className="font-semibold mb-3">Other Income/Expenses</h4>
        <div className="space-y-1">
          {data.otherIncome.length > 0 && (
            <>
              <div className="font-medium mt-2">Other Income</div>
              {data.otherIncome.map((row, idx) => (
                <AccountRow key={idx} row={row} startDate={data.startDate} endDate={data.endDate} />
              ))}
            </>
          )}
          {data.otherExpenses.length > 0 && (
            <>
              <div className="font-medium mt-2">Other Expenses</div>
              {data.otherExpenses.map((row, idx) => (
                <AccountRow key={idx} row={row} startDate={data.startDate} endDate={data.endDate} />
              ))}
            </>
          )}
          <div className="flex justify-between py-2 font-bold text-purple-600 border-t-2 mt-2">
            <span>Net Other</span>
            <span className="tabular-nums">{formatCurrency(netOther)}</span>
          </div>
          <div className={cn('flex justify-between py-2 font-bold border-t-2 mt-4', data.netIncome >= 0 ? 'text-green-700' : 'text-red-600')}>
            <span>NET INCOME</span>
            <span className="tabular-nums">{formatCurrency(data.netIncome)}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
